import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import type { AuthUser, ProfileData, Inbox, Agent, AgentFull, AgentReport, TeamWithMembers, ChatwootSession } from '@/types/auth'

// Contas com muitos times (50+) já travaram o Chatwoot self-hosted disparando
// 1 request de team_members por time TODAS de uma vez. Processa em lotes
// pequenos. Ver também @/composables/useTeamsWithMembers.ts — cacheia o
// resultado de fetchAllTeamsWithMembers por sessão pra não repetir essa
// varredura em toda tela que precisa de membership de time.
const TEAM_MEMBERS_CONCURRENCY = 5

async function mapWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = []
  for (let i = 0; i < items.length; i += limit) {
    const batch = items.slice(i, i + limit)
    results.push(...(await Promise.all(batch.map(fn))))
  }
  return results
}

export async function fetchProfile(token: string): Promise<ProfileData> {
  const res = await fetch(`${CHATWOOT_BASE}/api/v1/profile`, {
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error('Falha ao carregar perfil')
  return res.json()
}

export async function updateProfile(token: string, data: {
  name?: string
  display_name?: string
  message_signature?: string
  availability?: string
}): Promise<ProfileData> {
  const res = await fetch(`${CHATWOOT_BASE}/api/v1/profile`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ profile: data }),
  })
  if (!res.ok) throw new Error('Falha ao atualizar perfil')
  return res.json()
}

// Sessões nativas do Chatwoot (web/Android/iPhone) do PRÓPRIO usuário dono
// de `token` — a API do Chatwoot não tem escopo admin pra ver sessão de
// outro agente, só self-service (ver ProfileView.vue e migration v30, que
// cobre só logins pela dashboard — coisas diferentes e complementares).
export async function fetchProfileSessions(token: string): Promise<ChatwootSession[]> {
  const res = await fetch(`${CHATWOOT_BASE}/api/v1/profile/sessions`, {
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error('Falha ao carregar sessões')
  return res.json()
}

export async function revokeProfileSession(token: string, sessionId: number): Promise<void> {
  const res = await fetch(`${CHATWOOT_BASE}/api/v1/profile/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  if (!res.ok) throw new Error('Falha ao revogar sessão')
}

export async function updateProfileAvatar(token: string, file: File): Promise<ProfileData> {
  const form = new FormData()
  form.append('profile[avatar]', file)
  const res = await fetch(`${CHATWOOT_BASE}/api/v1/profile`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: form,
  })
  if (!res.ok) throw new Error('Falha ao atualizar avatar')
  return res.json()
}

export async function fetchUserInboxes(accountId: number, token: string, userId: number): Promise<Inbox[]> {
  try {
    const res = await fetch(`${CHATWOOT_BASE}/api/v1/accounts/${accountId}/inboxes`, {
      headers: authHeaders(token),
    })
    if (!res.ok) return []
    const data = await res.json()
    const all: any[] = data?.payload ?? data
    if (!Array.isArray(all) || !all.length) return []

    const toInbox = (i: any): Inbox => ({
      id: i.id, name: i.name, channel_type: i.channel_type, avatar_url: i.avatar_url,
    })

    // Tenta filtrar pelos canais onde o usuário é membro
    const checks = await Promise.all(
      all.map(async (inbox) => {
        try {
          const r = await fetch(
            `${CHATWOOT_BASE}/api/v1/accounts/${accountId}/inbox_members/${inbox.id}`,
            { headers: authHeaders(token) }
          )
          if (!r.ok) return { inbox, isMember: true } // sem acesso à lista → assume membro (admin)
          const body = await r.json()
          const members: any[] = body?.payload ?? body
          if (!Array.isArray(members)) return { inbox, isMember: true }
          const isMember = members.some(m => m.id === userId)
          return { inbox, isMember }
        } catch { return { inbox, isMember: true } }
      })
    )

    const filtered = checks.filter(c => c.isMember).map(c => toInbox(c.inbox))
    // Se nenhum canal retornou como membro via API, mostra todos (fallback para admin)
    return filtered.length > 0 ? filtered : all.map(toInbox)
  } catch {
    return []
  }
}

/**
 * O Chatwoot recusa um novo login com 409 quando o usuário já bate no teto
 * de sessões simultâneas (MAX_SESSIONS, self-hosted) — nesse caso o corpo do
 * 409 já vem com { sessions_limit_reached: true, sessions: [...] }, sem
 * precisar de token pra buscar (a picker é parte da própria resposta de
 * erro do sign_in). LoginView.vue mostra essas sessões com botão de
 * revogar; escolhida uma (ou "revogar todas"), o retry do sign_in leva
 * revoke_session_id/revoke_all_sessions no body e completa o login.
 */
export class SessionsLimitError extends Error {
  sessions: ChatwootSession[]
  constructor(sessions: ChatwootSession[]) {
    super('sessions_limit_reached')
    this.name = 'SessionsLimitError'
    this.sessions = sessions
  }
}

export async function signIn(
  email: string,
  password: string,
  revoke?: { sessionId?: number; allSessions?: boolean },
): Promise<AuthUser> {
  const res = await fetch(`${CHATWOOT_BASE}/auth/sign_in`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      password,
      ...(revoke?.sessionId ? { revoke_session_id: revoke.sessionId } : {}),
      ...(revoke?.allSessions ? { revoke_all_sessions: true } : {}),
    }),
  })

  if (!res.ok) {
    if (res.status === 409) {
      const err = await res.json().catch(() => ({}))
      if (err?.sessions_limit_reached) throw new SessionsLimitError(err.sessions || [])
    }
    if (res.status === 429) {
      throw new Error('Muitas tentativas de login. Aguarde alguns instantes e tente novamente.')
    }
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error || 'Credenciais inválidas')
  }

  const json = await res.json()
  const d = json.data

  return {
    access_token: d.access_token,
    account_id: d.account_id,
    id: d.id,
    name: d.name || d.available_name || d.email,
    email: d.email,
    avatar_url: d.avatar_url || '',
    role: d.accounts?.[0]?.role || d.role || 'agent',
    pubsub_token: d.pubsub_token,
  }
}

export async function fetchAgents(accountId: number, token: string): Promise<Agent[]> {
  try {
    const res = await fetch(`${CHATWOOT_BASE}/api/v1/accounts/${accountId}/agents?api_access_token=${token}`)
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data)
      ? data.map((a: any) => ({ id: a.id, name: a.name || a.available_name || a.email, email: a.email, avatar_url: a.thumbnail || a.avatar_url || '' }))
      : []
  } catch {
    return []
  }
}

export async function fetchAgentsFull(accountId: number, token: string): Promise<AgentFull[]> {
  try {
    const res = await fetch(`${CHATWOOT_BASE}/api/v1/accounts/${accountId}/agents`, {
      headers: authHeaders(token),
    })
    if (!res.ok) return []
    const data = await res.json()
    return Array.isArray(data)
      ? data.map((a: any) => ({
          id: a.id,
          name: a.name || a.available_name || a.email,
          email: a.email,
          avatar_url: a.thumbnail || a.avatar_url || '',
          role: a.role || 'agent',
          availability_status: a.availability_status || 'offline',
          created_at: a.created_at || '',
        }))
      : []
  } catch {
    return []
  }
}

export async function fetchAgentReport(accountId: number, token: string, agentId: number): Promise<AgentReport> {
  try {
    const since = Math.floor(new Date(new Date().getFullYear(), 0, 1).getTime() / 1000)
    const until = Math.floor(Date.now() / 1000)
    const res = await fetch(
      `${CHATWOOT_BASE}/api/v1/accounts/${accountId}/reports/summary?type=agent&id=${agentId}&since=${since}&until=${until}`,
      { headers: authHeaders(token) },
    )
    if (!res.ok) return { conversations_count: 0, avg_first_response_time: 0 }
    const d = await res.json()
    return {
      conversations_count: d.conversations_count ?? 0,
      avg_first_response_time: d.avg_first_response_time ?? 0,
    }
  } catch {
    return { conversations_count: 0, avg_first_response_time: 0 }
  }
}

export async function fetchAllTeamsWithMembers(accountId: number, token: string): Promise<TeamWithMembers[]> {
  try {
    const res = await fetch(`${CHATWOOT_BASE}/api/v1/accounts/${accountId}/teams`, {
      headers: authHeaders(token),
    })
    if (!res.ok) return []
    const teams: any[] = await res.json()
    if (!Array.isArray(teams)) return []
    return mapWithConcurrency(teams, TEAM_MEMBERS_CONCURRENCY, async (t) => {
      try {
        const r = await fetch(
          `${CHATWOOT_BASE}/api/v1/accounts/${accountId}/teams/${t.id}/team_members`,
          { headers: authHeaders(token) },
        )
        const members: any[] = r.ok ? await r.json() : []
        return {
          id: t.id,
          name: t.name,
          memberIds: Array.isArray(members) ? members.map((m: any) => m.id) : [],
        }
      } catch {
        return { id: t.id, name: t.name, memberIds: [] }
      }
    })
  } catch {
    return []
  }
}
